/**
 * Google Analytics 4 (GA4) tracking provider
 */

import type {
  TrackingProvider,
  UserProperties,
  GroupProperties,
} from '../types'

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID

let initialized = false
let initPromise: Promise<void> | null = null

function loadScript(id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load gtag.js'))
    document.head.appendChild(script)
  })
}

export function createGA4Provider(): TrackingProvider {
  async function ensureInit(): Promise<void> {
    if (initialized) return
    if (!MEASUREMENT_ID) return

    if (!initPromise) {
      initPromise = (async () => {
        window.dataLayer = window.dataLayer || []
        // Must use `arguments` (not rest params) — gtag.js expects Arguments objects in dataLayer
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(window as any).gtag = function () {
          window.dataLayer.push(arguments) // eslint-disable-line prefer-rest-params
        }
        gtag('js', new Date())
        gtag('config', MEASUREMENT_ID, { send_page_view: false })

        await loadScript(MEASUREMENT_ID)
        initialized = true
      })()
    }

    await initPromise
  }

  return {
    name: 'ga4',

    async init(): Promise<void> {
      await ensureInit()
    },

    async reset(): Promise<void> {
      await ensureInit()
      if (!MEASUREMENT_ID) return
      gtag('config', MEASUREMENT_ID, { user_id: undefined })
      gtag('set', 'user_properties', {
        user_name: undefined,
        user_email: undefined,
      })
    },

    async track(
      eventName: string,
      properties: Record<string, unknown>
    ): Promise<void> {
      await ensureInit()
      if (!MEASUREMENT_ID) return
      gtag('event', eventName, properties)
    },

    async identify(userId: string, properties?: UserProperties): Promise<void> {
      await ensureInit()
      if (!MEASUREMENT_ID) return
      gtag('config', MEASUREMENT_ID, { user_id: userId })
      const userProperties: Record<string, unknown> = {}
      if (properties?.name) userProperties.user_name = properties.name
      if (properties?.email) userProperties.user_email = properties.email
      if (Object.keys(userProperties).length > 0) {
        gtag('set', 'user_properties', userProperties)
      }
    },

    async setGroup(
      _groupKey: string,
      _groupId: string | string[]
    ): Promise<void> {
      // GA4 has no group concept
    },

    async identifyGroup(
      _groupKey: string,
      _groupId: string,
      _properties?: GroupProperties
    ): Promise<void> {
      // GA4 has no group concept
    },

    async clearGroup(_groupKey: string): Promise<void> {
      // GA4 has no group concept
    },
  }
}
